import React, { useEffect, useRef, useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { validateFuncForRider } from '../../constraints/constraints'
import { createStaff, editStaff, getStaffs } from '../../apollo'
import { PERMISSIONS } from '../../utils/permissions'
import useStyles from './styles'
import useGlobalStyles from '../../utils/globalStyles'
import FormCardHeader from '../FormCardHeader'
import {
  Box,
  Switch,
  Typography,
  Input,
  Alert,
  Button,
  Grid,
  Checkbox,
  Select,
  MenuItem,
  ListItemText
} from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'

const CREATE_STAFF = gql`
  ${createStaff}
`
const EDIT_STAFF = gql`
  ${editStaff}
`
const GET_STAFFS = gql`
  ${getStaffs}
`

function Staff(props) {
  const formRef = useRef()
  const mutation = props.staff ? EDIT_STAFF : CREATE_STAFF
  const initialValuesRef = useRef(null)

  const name = props.staff ? props.staff.name : ''
  const email = props.staff ? props.staff.email : ''
  const password = props.staff ? props.staff.plainPassword || '' : ''
  const phone = props.staff ? props.staff.phone : ''
  const [mainError, mainErrorSetter] = useState('')
  const [success, successSetter] = useState('')
  const [nameError, nameErrorSetter] = useState(null)
  const [emailError, emailErrorSetter] = useState(null)
  const [passwordError, passwordErrorSetter] = useState(null)
  const [confirmPasswordError, confirmPasswordErrorSetter] = useState(null)
  const [phoneError, phoneErrorSetter] = useState(null)
  const [permissionsError, permissionsErrorSetter] = useState(null)
  const [permissions, setPermissions] = useState(
    props.staff && props.staff.permissions ? props.staff.permissions : []
  )
  const [showPassword, setShowPassword] = useState(false)
  const [isActive, setIsActive] = useState(
    props.staff ? props.staff.isActive : true
  )
  const [isDirty, setIsDirty] = useState(!props.staff)

  const onCompleted = () => {
    if (!props.staff) clearFields()
    const message = props.staff
      ? t('StaffUpdatedSuccessfully')
      : t('StaffAddedSuccessfully')
    mainErrorSetter('')
    successSetter(message)
    setTimeout(hideAlert, 3000)
    if (props.onClose) {
      setTimeout(() => {
        props.onClose()
      }, 3500)
    }
  }

  const onError = ({ graphQLErrors, networkError }) => {
    if (graphQLErrors && graphQLErrors.length) {
      mainErrorSetter(graphQLErrors[0].message)
    }
    if (networkError) {
      mainErrorSetter(networkError.result.errors[0].message)
    }
    successSetter('')
    setTimeout(hideAlert, 3000)
  }

  const [mutate, { loading }] = useMutation(mutation, {
    refetchQueries: [{ query: GET_STAFFS }],
    onError,
    onCompleted
  })

  useEffect(() => {
    setPermissions(
      props.staff && props.staff.permissions ? props.staff.permissions : []
    )
    setIsActive(props.staff ? props.staff.isActive : true)
    initialValuesRef.current = {
      name: props.staff ? props.staff.name || '' : '',
      email: props.staff ? props.staff.email || '' : '',
      password: props.staff ? props.staff.plainPassword || '' : '',
      confirmPassword: props.staff ? props.staff.plainPassword || '' : '',
      phone: props.staff ? String(props.staff.phone || '') : '',
      isActive: props.staff ? props.staff.isActive : true,
      permissions:
        props.staff && props.staff.permissions
          ? [...props.staff.permissions].sort()
          : []
    }
    setIsDirty(!props.staff)
  }, [props.staff])

  const getCurrentValues = (overrides = {}) => {
    const form = formRef.current
    return {
      name: form?.['input-name']?.value || '',
      email: form?.['input-email']?.value || '',
      password: form?.['input-password']?.value || '',
      confirmPassword: form?.['input-confirm-password']?.value || '',
      phone: form?.['input-phone']?.value || '',
      isActive,
      permissions: [...permissions].sort(),
      ...overrides
    }
  }

  // Pass freshly-set state as overrides: setState is async, so reading it
  // here would compare the previous value.
  const updateDirty = (overrides = {}) => {
    if (!props.staff || !initialValuesRef.current) {
      setIsDirty(true)
      return
    }
    const current = getCurrentValues(overrides)
    setIsDirty(
      JSON.stringify(current) !== JSON.stringify(initialValuesRef.current)
    )
  }

  const onBlur = (setter, field, state, extraValues = {}) => {
    const validationResult = validateFuncForRider(
      { [field]: state, ...extraValues },
      field
    )
    setter(validationResult.isValid)
  }

  const onSubmitValidation = () => {
    const nameValue = formRef.current['input-name'].value
    const emailValue = formRef.current['input-email'].value
    const passwordValue = formRef.current['input-password'].value
    const confirmValue = formRef.current['input-confirm-password'].value
    const phoneValue = formRef.current['input-phone'].value

    const nameValidation = validateFuncForRider({ name: nameValue }, 'name')
    const emailValidation = validateFuncForRider({ email: emailValue }, 'email')
    const passwordValidation = validateFuncForRider(
      { password: passwordValue },
      'password'
    )
    const confirmValidation = validateFuncForRider(
      { confirmPassword: confirmValue, password: passwordValue },
      'confirmPassword'
    )
    const phoneValidation = validateFuncForRider({ phone: phoneValue }, 'phone')
    const permissionsValid = permissions.length > 0

    nameErrorSetter(nameValidation.isValid)
    emailErrorSetter(emailValidation.isValid)
    passwordErrorSetter(passwordValidation.isValid)
    confirmPasswordErrorSetter(confirmValidation.isValid)
    phoneErrorSetter(phoneValidation.isValid)
    permissionsErrorSetter(permissionsValid)

    return (
      nameValidation.isValid &&
      emailValidation.isValid &&
      passwordValidation.isValid &&
      confirmValidation.isValid &&
      phoneValidation.isValid &&
      permissionsValid
    )
  }

  const clearFields = () => {
    formRef.current.reset()
    nameErrorSetter(null)
    emailErrorSetter(null)
    passwordErrorSetter(null)
    confirmPasswordErrorSetter(null)
    phoneErrorSetter(null)
    permissionsErrorSetter(null)
    setPermissions([])
  }

  const hideAlert = () => {
    mainErrorSetter('')
    successSetter('')
  }

  const { t } = props
  const classes = useStyles()
  const globalClasses = useGlobalStyles()

  return (
    <Box container className={classes.container}>
      <FormCardHeader
        className={classes.heading}
        title={`${props.staff ? t('Edit') : t('Add')} ${t('Staff')}`}
        onClose={props.onClose}
      />

      <Box className={classes.form}>
        <form ref={formRef}>
          <Grid container columnSpacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.labelText}>{t('Name')}</Typography>
              <Input
                style={{ marginTop: -1 }}
                id="input-name"
                name="input-name"
                placeholder={t('Name')}
                type="text"
                defaultValue={name}
                onChange={updateDirty}
                onBlur={event => {
                  onBlur(nameErrorSetter, 'name', event.target.value)
                }}
                disableUnderline
                className={[
                  globalClasses.input,
                  nameError === false
                    ? globalClasses.inputError
                    : nameError === true
                    ? globalClasses.inputSuccess
                    : ''
                ]}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.labelText}>
                {t('Email')}
              </Typography>
              <Input
                style={{ marginTop: -1 }}
                id="input-email"
                name="input-email"
                placeholder={t('Email')}
                type="email"
                defaultValue={email}
                onChange={updateDirty}
                onBlur={event => {
                  onBlur(emailErrorSetter, 'email', event.target.value)
                }}
                disableUnderline
                className={[
                  globalClasses.input,
                  emailError === false
                    ? globalClasses.inputError
                    : emailError === true
                    ? globalClasses.inputSuccess
                    : ''
                ]}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.labelText}>
                {t('Password')}
              </Typography>
              <Input
                style={{ marginTop: -1 }}
                id="input-password"
                name="input-password"
                placeholder={t('Password')}
                type={showPassword ? 'text' : 'password'}
                defaultValue={password}
                onChange={updateDirty}
                onBlur={event =>
                  onBlur(passwordErrorSetter, 'password', event.target.value)
                }
                disableUnderline
                className={[
                  globalClasses.input,
                  passwordError === false
                    ? globalClasses.inputError
                    : passwordError === true
                    ? globalClasses.inputSuccess
                    : ''
                ]}
                endAdornment={
                  <InputAdornment position="end">
                    <Checkbox
                      checked={showPassword}
                      onChange={() => setShowPassword(!showPassword)}
                      color="primary"
                      icon={<VisibilityOffIcon />}
                      checkedIcon={<VisibilityIcon />}
                    />
                  </InputAdornment>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.labelText}>
                {t('ConfirmPassword')}
              </Typography>
              <Input
                style={{ marginTop: -1 }}
                id="input-confirm-password"
                name="input-confirm-password"
                placeholder={t('ConfirmPassword')}
                type={showPassword ? 'text' : 'password'}
                defaultValue={password}
                onChange={updateDirty}
                onBlur={event =>
                  onBlur(
                    confirmPasswordErrorSetter,
                    'confirmPassword',
                    event.target.value,
                    {
                      password: formRef.current['input-password'].value
                    }
                  )
                }
                disableUnderline
                className={[
                  globalClasses.input,
                  confirmPasswordError === false
                    ? globalClasses.inputError
                    : confirmPasswordError === true
                    ? globalClasses.inputSuccess
                    : ''
                ]}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.labelText}>
                {t('PhoneNumber')}
              </Typography>
              <Input
                style={{ marginTop: -1 }}
                id="input-phone"
                name="input-phone"
                placeholder={t('PhoneNumber')}
                type="tel"
                defaultValue={phone}
                onChange={updateDirty}
                onBlur={event =>
                  onBlur(phoneErrorSetter, 'phone', event.target.value)
                }
                disableUnderline
                className={[
                  globalClasses.input,
                  phoneError === false
                    ? globalClasses.inputError
                    : phoneError === true
                    ? globalClasses.inputSuccess
                    : ''
                ]}
                pattern="[0-9]*"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.labelText} sx={{ mt: 0, mb: 0 }}>
                {t('Permissions')}
              </Typography>
              <Select
                id="input-permissions"
                name="input-permissions"
                multiple
                displayEmpty
                value={permissions}
                onChange={event => {
                  const nextValue = event.target.value
                  const normalized =
                    typeof nextValue === 'string'
                      ? nextValue.split(',')
                      : nextValue
                  const isAdminSelected = normalized.includes('Admin')
                  const wasAdminSelected = permissions.includes('Admin')
                  const allCodes = PERMISSIONS.map(option => option.code)
                  let nextPermissions = normalized

                  if (isAdminSelected) {
                    nextPermissions = allCodes
                  } else if (wasAdminSelected) {
                    nextPermissions = []
                  } else {
                    nextPermissions = normalized.filter(
                      option => option !== 'Admin'
                    )
                  }

                  setPermissions(nextPermissions)
                  permissionsErrorSetter(nextPermissions.length > 0)
                  updateDirty({ permissions: [...nextPermissions].sort() })
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { maxHeight: 320 }
                  }
                }}
                renderValue={selected =>
                  selected.length ? selected.join(', ') : t('Permissions')
                }
                sx={{ mt: 0, color: 'black' }}
                style={{ marginTop: 0 }}
                className={[
                  globalClasses.input,
                  permissionsError === false
                    ? globalClasses.inputError
                    : permissionsError === true
                    ? globalClasses.inputSuccess
                    : ''
                ]}>
                {PERMISSIONS.map(option => (
                  <MenuItem
                    key={option.code}
                    value={option.code}
                    sx={{ color: 'black' }}>
                    <Checkbox checked={permissions.indexOf(option.code) > -1} />
                    <ListItemText primary={t(option.label)} />
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>

          <Box className={globalClasses.formActions}>
            <Button
              className={globalClasses.button}
              disabled={loading || (props.staff ? !isDirty : false)}
              onClick={async e => {
                e.preventDefault()
                if (onSubmitValidation()) {
                  mutate({
                    variables: {
                      staffInput: {
                        _id: props.staff ? props.staff._id : '',
                        name: formRef.current['input-name'].value,
                        email: formRef.current['input-email'].value,
                        password: formRef.current['input-password'].value,
                        phone: formRef.current['input-phone'].value,
                        isActive,
                        permissions
                      }
                    }
                  })
                }
              }}>
              {t('Save')}
            </Button>
          </Box>
        </form>
      </Box>

      <Box mt={2}>
        {success && (
          <Alert
            className={globalClasses.alertSuccess}
            variant="filled"
            severity="success">
            {success}
          </Alert>
        )}
        {mainError && (
          <Alert
            className={globalClasses.alertSuccess}
            variant="filled"
            severity="error">
            {mainError}
          </Alert>
        )}
      </Box>
    </Box>
  )
}

export default withTranslation()(Staff)
